import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";
import { SiteService } from "../../core/services/site.service";
import { SiteDropdownOption } from "./site-dropdown-option.model";
import { AlertService } from "../alert/alert.service";
import { debounceTime } from "rxjs/internal/operators/debounceTime";
import { LogHelper } from "../../core/helpers/log.helper";
import { SiteDropdownChanged } from "./site-dropdown-changed.model";

@Component({
  selector: 'app-site-dropdown',
  templateUrl: './site-dropdown.component.html',
  styleUrl: './site-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SiteDropdownComponent),
      multi: true
    }
  ]
})
export class SiteDropdownComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnChanges {
  protected _siteService = inject(SiteService);
  protected _alertService = inject(AlertService);

  @ViewChild('searchTermInput') input: ElementRef;

  @Output() cleared = new EventEmitter();
  @Output() selected = new EventEmitter<SiteDropdownChanged>();

  @Input() trialId: string;
  @Input() approvedOnly: boolean = true;
  @Input() assignedOnly: boolean = true;
  @Input() unassignedOnly: boolean = false;
  @Input() trialIdRequired: boolean = true;
  @Input() countryFilter: string;
  @Input() siteIdFilter: string[] | null = null;

  @Input() formControlName!: string;
  @Input() placeholder: string = 'Select site...';
  @Input() truncate: number = 35;
  @Input() siteNumberVisible = true;
  @Input() siteAddressVisible = true;
  @Input() siteConsumerNameVisible = true;
  @Input() siteNameVisible = true;
  @Input() siteCountryVisible = true;
  @Input() disableWhenTrialNotSet = true;
  @Input() clearable: boolean = true;

  protected filterForm = new FormGroup({
    searchTerm: new FormControl<string>('')
  });
  protected value: any;
  protected dropdownIsVisible = false;
  public selectedSite: SiteDropdownOption;
  protected searchHasValue = false;
  protected formGroup: FormGroup;
  protected formControl: FormControl;
  filteredSites: SiteDropdownOption[] = [];
  isLoading = false;
  protected minSearchTermCharacters: number = 3;
  protected initialised = false;
  protected displayValue: string = '';

  constructor(
    @Optional() private controlContainer: ControlContainer,
    private elementRef: ElementRef
  ) {

  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: HTMLElement): void {
    if (this.dropdownIsVisible && !this.elementRef.nativeElement.contains(target)) {
      this.dropdownIsVisible = false;
    }
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    // Get the form control and the parent form group
    this.formGroup = this.controlContainer?.control as FormGroup | null;
    this.formControl = this.formGroup.get(this.formControlName) as FormControl | null;

    // Listen to the search term input for changes, and
    this.filterForm.get('searchTerm')?.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value: string) => {
        if (value && value.length >= this.minSearchTermCharacters) {
          this.loadSites();
        } else if (value === '') {
          this.loadSites();
        }
      });

    // Listens for changes to the value of the main form control. If its the first change, i.e. an initial value
    // it will try to search for the patient and preselect that patient
    this.formControl.valueChanges.subscribe(value => {
      if (!this.initialised) {
        this.initialised = true;
      }

      if (value !== this.value && value !== '') {
        this.value = value;
        this.loadSites();
      }

      if (!value || value === '') {
        this.selectedSite = undefined;
        this.displayValue = '';
      }
    });

    if (this.disableWhenTrialNotSet) {
      Promise.resolve().then(() => this.formControl.disable());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['trialId'] && changes['trialId'].currentValue !== changes['trialId'].previousValue) {
      this.selectedSite = undefined;
      this.displayValue = '';
      this.loadSites();
    }

    if (changes['countryFilter'] && !changes['countryFilter'].firstChange
      && changes['countryFilter'].currentValue !== changes['countryFilter'].previousValue) {
      if (this.selectedSite && this.countryFilter
        && this.selectedSite.country !== this.countryFilter) {
        this.reset();
      }
      this.loadSites();
    }

    if (changes['siteIdFilter'] && !changes['siteIdFilter'].firstChange) {
      if (this.selectedSite && this.siteIdFilter
        && !this.siteIdFilter.includes(this.selectedSite.id)) {
        this.reset();
      }
      this.loadSites();
    }

    // Enable/disable control based
    this.enableDisableControl();

    this.initialised = true;
  }

  private enableDisableControl() {
    if (!this.formControl) {
      return;
    }
    Promise.resolve().then(() => {
      if (this.disableWhenTrialNotSet) {
        if (this.trialId && this.trialId !== '') {
          this.formControl.enable();
        } else {
          this.formControl.setValue('');
          this.selectedSite = null;
          this.displayValue = '';
          this.formControl.disable();
        }
      } else {
        this.formControl.enable();
      }
    });
  }

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  reset() {
    this.selectedSite = null;
    this.displayValue = '';
    this.formControl.setValue('');
  }

  protected onOpenDropdown(): void {
    if (!this.formControl.disabled) {
      this.dropdownIsVisible = !this.dropdownIsVisible;

      if (this.dropdownIsVisible) {
        setTimeout(() => {
          this.input?.nativeElement.focus();
        }, 500);
      }
    }
  }

  public setSite(siteId: string): void {
    if (!siteId) {
      return;
    }

    const option = this.filteredSites.find(x => x.id === siteId);
    if (option) {
      this.onSelectSite(option);
    }
  }

  public onSelectSite(option: SiteDropdownOption): void {
    this.selectedSite = option;
    this.value = option.id;

    let displayValue = option.name;
    if (this.siteCountryVisible)
      displayValue += ' ' + option.country;

    if (this.siteNumberVisible && option.trialSiteNumber)
      displayValue = option.trialSiteNumber + ' ' + displayValue;

    this.displayValue = displayValue;
    this.formControl.setValue(this.value);
    this.formControl.updateValueAndValidity();

    // Emit the object that has been selected
    const changed = SiteDropdownChanged.create(option, displayValue);
    this.selected.emit(changed);
  }

  /**
   * Clears the selected trial
   */
  protected onClearSelection(event: MouseEvent, closeDropdown = false): void {
    event.stopPropagation();

    if (this.formControl.disabled) {
      return;
    }

    this.selectedSite = undefined;
    this.filterForm.reset();
    this.formControl.reset();
    this.cleared.emit();

    if (closeDropdown) {
      setTimeout(() => {
        this.dropdownIsVisible = false
      }, 100);
    }
  }

  private loadSites(): void {
    if (this.trialIdRequired && !this.trialId) {
      return;
    }

    this.isLoading = true;
    this._siteService.siteDropdownResults(this.trialId, this.filterForm.get('searchTerm').value, null, this.approvedOnly, this.assignedOnly, this.unassignedOnly).subscribe({
      next: sites => {
        const seen = new Set<string>();
        sites = sites.filter(s => {
          if (seen.has(s.id)) {
            return false;
          }
          seen.add(s.id);
          return true;
        });

        if (this.countryFilter) {
          sites = sites.filter(s => s.country === this.countryFilter);
        }

        if (this.siteIdFilter) {
          const allowed = new Set(this.siteIdFilter);
          sites = sites.filter(s => allowed.has(s.id));
        }

        if (this.formControl.value) {
          const selected = sites.find(x => x.id === this.formControl.value);
          if (selected) {
            this.onSelectSite(selected);
          }
        }

        this.filteredSites = sites;
        this.isLoading = false;

        // Toggle enable/disable control
        this.enableDisableControl();
      },
      error: error => {
        LogHelper.log(error);
        this._alertService.showErrorAlert(error);
        this.isLoading = false;
      }
    });
  }

}
